<script>
  import './ZenSplit.css';
  import { onDestroy } from 'svelte';
  import { Splitpanes } from 'svelte-splitpanes';
  import { writable } from 'svelte/store';
  import { persist } from '$lib/stores/face-store.js';

  /**
   * PROPS
   */
  export let key = '';
  export let horizontal = false;
  export let defaultSizes = [];
  export let prefix = 'layout__';

  // Layout behavior
  export let mode = 'fixed'; // 'fixed' | 'resizable'
  export let lock = undefined;
  export let persistLayout = undefined;

  // UI
  export let noGrip = true;
  export let gripPosition = 'center'; // 'start' | 'center' | 'end' | 'full'
  export let splitTheme = 'clean';

  /**
   * STATE
   */
  let paneSizes = [];
  let unsubscribe;
  let layoutStore; // ⭐ KHÔNG tạo reactive vô tội vạ

  // ⭐ Reactive lock logic
  $: isLocked = lock !== undefined ? lock : mode === 'fixed';

  $: storageKey = `${prefix}${key}`;

  // ⭐ Persist rule
  $: shouldPersist = persistLayout !== undefined ? persistLayout : mode === 'fixed';
  $: normalizedGripPosition = ['start', 'center', 'end', 'full'].includes(gripPosition) ? gripPosition : 'center';
  $: normalizedSplitTheme = ['clean', 'soft', 'glass', 'graphite'].includes(splitTheme) ? splitTheme : 'clean';

  /**
   * ⭐ TẠO STORE CHỈ KHI key / persist rule thay đổi
   * KHÔNG phụ thuộc defaultSizes (đây là bug gốc của bạn)
   */
  let prevStoreKey = '';

  $: {
    const storeKey = `${storageKey}-${shouldPersist}`;

    if (storeKey !== prevStoreKey) {
      prevStoreKey = storeKey;

      // hủy subscribe cũ
      unsubscribe && unsubscribe();

      // tạo store mới
      if (shouldPersist && key) {
        layoutStore = persist(storageKey, defaultSizes);
      } else {
        layoutStore = writable(defaultSizes);
      }

      // ⭐ SUBSCRIBE LẠI vào store mới (CỰC KỲ QUAN TRỌNG)
      unsubscribe = layoutStore.subscribe((value) => {
        const source = Array.isArray(value) && value.length ? value : defaultSizes;

        paneSizes = normalizeSizes(source);
      });
    }
  }

  onDestroy(() => {
    unsubscribe && unsubscribe();
  });

  /**
   * ⭐ CRITICAL FIX:
   * So sánh DEEP defaultSizes (KHÔNG dùng length)
   * Vì toggleRight: length vẫn = 3 nhưng size thay đổi (22 -> 0)
   */
  let prevDefaultHash = '';

  $: {
    const defaultHash = JSON.stringify(defaultSizes || []);

    if (defaultHash !== prevDefaultHash) {
      prevDefaultHash = defaultHash;

      // ⭐ Reset layout theo defaultSizes mới
      // Đây là chỗ toggleRight phải tác động
      layoutStore?.set?.(defaultSizes);
    }
  }

  /**
   * Normalize pane config cho Splitpanes
   */
  function normalizeSizes(arr) {
    if (!Array.isArray(arr)) return [];

    return arr.map((item = {}) => {
      const paneLocked = isLocked ? true : (item.locked ?? false);

      return {
        size: Number.isFinite(item.size) ? item.size : undefined,
        min: Number.isFinite(item.min) ? item.min : undefined,
        max: Number.isFinite(item.max) ? item.max : undefined,
        snap: item.snap ?? false,
        snapSize: Number.isFinite(item.snapSize) ? item.snapSize : undefined,
        locked: paneLocked,
        collapsible: item.collapsible ?? false
      };
    });
  }

  /**
   * Resize handler (chỉ khi resizable)
   */
  function handleResize(event) {
    if (isLocked) return;

    const sizes = event?.detail;
    if (!Array.isArray(sizes)) return;

    const result = sizes.map((item, index) => {
      const prev = paneSizes[index] || {};
      return {
        ...prev,
        size: Math.round(item?.size ?? prev.size ?? 0)
      };
    });

    layoutStore?.set?.(result);
  }

  /**
   * Root CSS class
   */
  $: rootClass = [
    'zen-split',
    `zen-theme-${normalizedSplitTheme}`,
    isLocked ? 'zen-split--locked' : 'zen-split--resizable',
    `zen-split--grip-${normalizedGripPosition}`,
    noGrip ? 'zen-split--no-grip' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Debug
  // $: console.log('defaultSizes:', defaultSizes);
  // $: console.log('paneSizes:', paneSizes);
</script>

<Splitpanes theme="" {horizontal} class={rootClass} on:resize={handleResize} {...$$restProps}>
  <slot {paneSizes} />
</Splitpanes>
